import {createContext, ReactNode, useContext, useState} from "react";
import {PlayerUltraLite} from "../interfaces";
import PlayerOverlay from "../players/PlayerOverlay";
import KickOverlay from "../bans/KickOverlay";
import BanOverlay from "../bans/BanOverlay";
import UnbanOverlay from "../bans/UnbanOverlay";
import IpBanOverlay from "../bans/IpBanOverlay";
import AlertOverlay from "./AlertOverlay";
import ConfirmOverlay, {ConfirmOverlayData} from "./ConfirmOverlay";
import SubscriptionLimitOverlay from "./SubscriptionLimitOverlay";
import ErrorOverlay from "./ErrorOverlay";
import {ErrorResponse, LimitErrorResponse} from "../../../util/fetch";
import GiveItemOverlay from "../players/inventory/GiveItemOverlay";

const Context = createContext<OverlayContextData>(null!)

export default function OverlayContext(props: { children: ReactNode }){

    const [playerOverlaySteamId, setPlayerOverlaySteamId] = useState<string>("")
    const [giveItemOverlayPlayer, setGiveItemOverlayPlayer] = useState<PlayerUltraLite>()
    const [banOverlayPlayer, setBanOverlayPlayer] = useState<PlayerUltraLite>()
    const [kickOverlayPlayer, setKickOverlayPlayer] = useState<PlayerUltraLite>()
    const [unbanOverlayPlayer, setUnbanOverlayPlayer] = useState<PlayerUltraLite>()
    const [ipBanOverlayData, setIpBanOverlayData] = useState<{ip?: string, show: boolean}>()
    const [ipUnbanOverlayData, setIpUnbanOverlayData] = useState<{ip?: string, show: boolean}>()
    const [alertOverlayData, setAlertOverlayData] = useState<{title: string, message: string}>()
    const [confirmOverlayData, setConfirmOverlayData] = useState<ConfirmOverlayData>()

    const [generalError, setGeneralError] = useState<ErrorResponse>()
    const [limitError, setLimitError] = useState<LimitErrorResponse>()

    const openErrorOverlay = (response: ErrorResponse) => {
        if (response.code === 67) {
            setLimitError(response as LimitErrorResponse)
        }
        else{
            setGeneralError(response)
        }
    }

    const context: OverlayContextData = {
        openPlayerOverlay: setPlayerOverlaySteamId,
        openGiveItemOverlay: setGiveItemOverlayPlayer,
        openKickOverlay: setKickOverlayPlayer,
        openBanOverlay: setBanOverlayPlayer,
        openUnbanOverlay: setUnbanOverlayPlayer,
        openIpBanOverlay: ip => setIpBanOverlayData({ip: ip, show: true}),
        openIpUnbanOverlay: ip => setIpUnbanOverlayData({ip: ip, show: true}),
        showAlert: (title, message) => setAlertOverlayData({title, message}),
        showConfirmation : (title, onConfirm, text, confirmText, cancelText) => setConfirmOverlayData({title, onConfirm, text, confirmText, cancelText}),
        openErrorOverlay: openErrorOverlay
    }

    return (
        <Context.Provider value={context}>
            {props.children}
            {playerOverlaySteamId && <PlayerOverlay steamId={playerOverlaySteamId} hide={() => setPlayerOverlaySteamId("")}/>}
            {giveItemOverlayPlayer && <GiveItemOverlay steamId={giveItemOverlayPlayer.steamId} playerName={giveItemOverlayPlayer.displayName} hide={() => setGiveItemOverlayPlayer(undefined)}/>}
            {kickOverlayPlayer && <KickOverlay playerName={kickOverlayPlayer.displayName} playerSteamId={kickOverlayPlayer.steamId} hide={() => setKickOverlayPlayer(undefined)} />}
            {banOverlayPlayer && <BanOverlay playerName={banOverlayPlayer.displayName} playerSteamId={banOverlayPlayer.steamId} playerIp={banOverlayPlayer.ip} hide={() => setBanOverlayPlayer(undefined)} />}
            {unbanOverlayPlayer && <UnbanOverlay playerName={unbanOverlayPlayer.displayName} playerSteamId={unbanOverlayPlayer.steamId} hide={() => setUnbanOverlayPlayer(undefined)} />}
            {ipBanOverlayData && <IpBanOverlay ip={ipBanOverlayData.ip} hide={() => setIpBanOverlayData(undefined)}/>}
            {ipUnbanOverlayData && <IpBanOverlay ip={ipUnbanOverlayData.ip} hide={() => setIpUnbanOverlayData(undefined)}/>}
            {alertOverlayData && <AlertOverlay message={alertOverlayData.message} title={alertOverlayData.title} hide={() => setAlertOverlayData(undefined)} />}
            {confirmOverlayData && <ConfirmOverlay data={confirmOverlayData} hide={() => setConfirmOverlayData(undefined)} />}
            {limitError && <SubscriptionLimitOverlay error={limitError} hide={() => setLimitError(undefined)} />}
            {generalError && <ErrorOverlay error={generalError} hide={() => setGeneralError(undefined)} />}
        </Context.Provider>
    )
}

export const useOverlay = () => useContext(Context)

interface OverlayContextData{
    openPlayerOverlay: (steamId: string) => void
    openGiveItemOverlay: (player: PlayerUltraLite) => void
    openKickOverlay: (player: PlayerUltraLite) => void
    openBanOverlay: (player: PlayerUltraLite) => void
    openUnbanOverlay: (player: PlayerUltraLite) => void
    openIpBanOverlay: (ip?: string) => void
    openIpUnbanOverlay: (ip?: string) => void
    showAlert: (title: string, message: string) => void
    showConfirmation: (title: string, onConfirm: () => Promise<string|undefined>, text?: string, confirmText?: string, cancelText?: string) => void
    openErrorOverlay: (response: ErrorResponse) => void
}